import { useEffect, useState, useMemo, memo } from "react"
import { db } from "../../../utils/firebase"

// ChartJS imports
import { Line } from "react-chartjs-2"
import { Chart as ChartJS, LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend } from "chart.js"

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend)

// Helper function to get color based on year offset
const getColorForYear = (yearOffset) => {
	if (yearOffset === 0) return { border: "#e41f13", bg: "#e41f13" }

	// Different colors for each historical year, with reduced opacity for visibility
	const colors = [
		{ r: 59, g: 130, b: 246 }, // Blue
		{ r: 168, g: 85, b: 247 }, // Purple
		{ r: 34, g: 197, b: 94 }, // Green
		{ r: 251, g: 146, b: 60 }, // Orange
		{ r: 20, g: 184, b: 166 }, // Teal
	]

	const colorIndex = Math.min(yearOffset - 1, colors.length - 1)
	const color = colors[colorIndex]
	const opacity = 0.6 // Consistent opacity for all historical years

	return {
		border: `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity})`,
		bg: `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity})`,
	}
}

const normalizeSite = (site) => {
	if (typeof site !== "string") return "Unknown"
	const trimmed = site.trim()
	return trimmed.length ? trimmed : "Unknown"
}

const dedupeByClient = (enquiries) => {
	const seen = new Set()
	const deduped = []

	enquiries.forEach((enquiry, index) => {
		const key = enquiry?.client || enquiry?.id || `idx-${index}`
		if (seen.has(key)) return
		seen.add(key)
		deduped.push(enquiry)
	})

	return deduped
}

const getColorForSite = (index, opacity) => {
	const colors = [
		{ r: 59, g: 130, b: 246 }, // Blue
		{ r: 168, g: 85, b: 247 }, // Purple
		{ r: 34, g: 197, b: 94 }, // Green
		{ r: 251, g: 146, b: 60 }, // Orange
		{ r: 20, g: 184, b: 166 }, // Teal
		{ r: 239, g: 68, b: 68 }, // Red
		{ r: 100, g: 116, b: 139 }, // Slate
	]

	const color = colors[index % colors.length]
	return `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity})`
}

function EnquiriesLineChart({ dateRanges, selectedYear, selectedMonth }) {
	const [enquiriesByYear, setEnquiriesByYear] = useState({})
	const [loading, setLoading] = useState(true)
	const [showSiteBreakdown, setShowSiteBreakdown] = useState(false)

	// Fetch data for all date ranges
	useEffect(() => {
		const fetchAllData = async () => {
			setLoading(true)
			const dataByYear = {}

			for (const range of dateRanges) {
				// Fetch active enquiries
				const enquiries = await db
					.collection("enquiries")
					.where("created", ">", range.start)
					.where("created", "<", range.end)
					.get()
					.then((enquiryDocs) => {
						const enquiriesList = []
						enquiryDocs.forEach((enquiryDoc) => {
							enquiriesList.push({ id: enquiryDoc.id, ...enquiryDoc.data() })
						})
						return enquiriesList
					})

				// Fetch deleted enquiries
				const deletedEnquiries = await db
					.collection("enquiries_removed")
					.where("enquiry.created", ">", range.start)
					.where("enquiry.created", "<", range.end)
					.get()
					.then((enquiryDocs) => {
						const enquiriesList = []
						enquiryDocs.forEach((enquiryDoc) => {
							enquiriesList.push({ id: enquiryDoc.id, ...enquiryDoc.data().enquiry })
						})
						return enquiriesList
					})

				const allEnquiries = [...enquiries, ...deletedEnquiries]

				dataByYear[range.yearOffset] = {
					enquiries: allEnquiries,
					count: allEnquiries.length,
					year: range.year,
				}
			}

			setEnquiriesByYear(dataByYear)
			setLoading(false)
		}

		fetchAllData()
	}, [dateRanges])

	// Process enquiries data with memoization
	const processEnquiries = useMemo(() => {
		return (data) => {
			const counts = {}
			const deduped = dedupeByClient(data)

			deduped.forEach((enquiry) => {
				const date = new Date(enquiry.created.seconds * 1000)
				const normalizedDate = date.toLocaleDateString("en-GB", {
					day: "2-digit",
					month: "2-digit",
				})

				counts[normalizedDate] = (counts[normalizedDate] || 0) + 1
			})

			// Sort dates and calculate cumulative enquiries
			const sortedDates = Object.keys(counts).sort((a, b) => {
				const [dayA, monthA] = a.split("/").map(Number)
				const [dayB, monthB] = b.split("/").map(Number)
				return monthA === monthB ? dayA - dayB : monthA - monthB
			})

			const cumulativeCounts = {}
			const dailyCounts = {}
			let runningTotal = 0
			sortedDates.forEach((date) => {
				runningTotal += counts[date]
				cumulativeCounts[date] = runningTotal
				dailyCounts[date] = counts[date]
			})

			return { cumulativeCounts, dailyCounts }
		}
	}, [])

	// Process enquiries data by site (daily + cumulative) with memoization
	const processEnquiriesBySite = useMemo(() => {
		return (data) => {
			const dailyCountsBySite = {}
			const totalsBySite = {}
			const deduped = dedupeByClient(data)

			deduped.forEach((enquiry) => {
				const date = new Date(enquiry.created.seconds * 1000)
				const normalizedDate = date.toLocaleDateString("en-GB", {
					day: "2-digit",
					month: "2-digit",
				})

				const site = normalizeSite(enquiry.site)

				if (!dailyCountsBySite[site]) dailyCountsBySite[site] = {}
				dailyCountsBySite[site][normalizedDate] = (dailyCountsBySite[site][normalizedDate] || 0) + 1
				totalsBySite[site] = (totalsBySite[site] || 0) + 1
			})

			const cumulativeCountsBySite = {}
			Object.keys(dailyCountsBySite).forEach((site) => {
				const counts = dailyCountsBySite[site]
				const sortedDates = Object.keys(counts).sort((a, b) => {
					const [dayA, monthA] = a.split("/").map(Number)
					const [dayB, monthB] = b.split("/").map(Number)
					return monthA === monthB ? dayA - dayB : monthA - monthB
				})

				let runningTotal = 0
				cumulativeCountsBySite[site] = {}
				sortedDates.forEach((date) => {
					runningTotal += counts[date]
					cumulativeCountsBySite[site][date] = runningTotal
				})
			})

			return { dailyCountsBySite, cumulativeCountsBySite, totalsBySite }
		}
	}, [])

	// Process data for all years with memoization
	const processedData = useMemo(() => {
		const processed = {}
		Object.keys(enquiriesByYear).forEach((yearOffset) => {
			processed[yearOffset] = processEnquiries(enquiriesByYear[yearOffset].enquiries)
		})
		return processed
	}, [enquiriesByYear, processEnquiries])

	// Process site-grouped data for all years
	const processedSiteData = useMemo(() => {
		const processed = {}
		Object.keys(enquiriesByYear).forEach((yearOffset) => {
			processed[yearOffset] = processEnquiriesBySite(enquiriesByYear[yearOffset].enquiries)
		})
		return processed
	}, [enquiriesByYear, processEnquiriesBySite])

	// Build overlay config: Top 5 sites + Other (ranked by current period)
	const siteOverlay = useMemo(() => {
		const current = processedSiteData[0]
		if (!current?.totalsBySite) {
			return {
				topSites: [],
				otherSites: [],
				hasOther: false,
				otherByYear: {},
			}
		}

		const ranked = Object.entries(current.totalsBySite).sort((a, b) => b[1] - a[1])
		const topSites = ranked.slice(0, 5).map(([site]) => site)
		const otherSites = ranked.slice(5).map(([site]) => site)

		const otherByYear = {}
		;[0, 1].forEach((yearOffset) => {
			const bySite = processedSiteData[yearOffset]?.dailyCountsBySite || {}
			const dailyCounts = {}

			otherSites.forEach((site) => {
				const siteDaily = bySite[site] || {}
				Object.keys(siteDaily).forEach((date) => {
					dailyCounts[date] = (dailyCounts[date] || 0) + (siteDaily[date] || 0)
				})
			})

			const sortedDates = Object.keys(dailyCounts).sort((a, b) => {
				const [dayA, monthA] = a.split("/").map(Number)
				const [dayB, monthB] = b.split("/").map(Number)
				return monthA === monthB ? dayA - dayB : monthA - monthB
			})

			let runningTotal = 0
			const cumulativeCounts = {}
			sortedDates.forEach((date) => {
				runningTotal += dailyCounts[date]
				cumulativeCounts[date] = runningTotal
			})

			otherByYear[yearOffset] = { dailyCounts, cumulativeCounts }
		})

		return {
			topSites,
			otherSites,
			hasOther: otherSites.length > 0,
			otherByYear,
		}
	}, [processedSiteData])

	// Get all unique dates with memoization
	const allDates = useMemo(() => {
		const datesSet = new Set()
		Object.values(processedData).forEach((yearData) => {
			Object.keys(yearData.cumulativeCounts).forEach((date) => datesSet.add(date))
		})

		return Array.from(datesSet).sort((a, b) => {
			const [dayA, monthA] = a.split("/").map(Number)
			const [dayB, monthB] = b.split("/").map(Number)
			return monthA === monthB ? dayA - dayB : monthA - monthB
		})
	}, [processedData])

	// Check if we're viewing the current month/year
	const isCurrentMonth = useMemo(() => {
		const now = new Date()
		return selectedYear === now.getFullYear() && selectedMonth === now.getMonth()
	}, [selectedYear, selectedMonth])

	// Get current date for comparison (only used if viewing current month)
	const today = useMemo(() => {
		return new Date().toLocaleDateString("en-GB", {
			day: "2-digit",
			month: "2-digit",
		})
	}, [])

	// Generate datasets with memoization
	const chartData = useMemo(() => {
		const datasets = []

		// Generate a dataset for each year
		Object.keys(enquiriesByYear)
			.sort((a, b) => parseInt(a) - parseInt(b))
			.forEach((yearOffset) => {
				const offset = parseInt(yearOffset)
				const yearData = processedData[offset]?.cumulativeCounts || {}
				const color = getColorForYear(offset)
				const year = enquiriesByYear[offset].year

				const data = allDates.map((date, index, dates) => {
					// For current year viewing current month, don't show future dates
					if (offset === 0 && isCurrentMonth && date > today) {
						return null
					}

					// Return the cumulative value or 0
					return yearData[date] || 0
				})

				datasets.push({
					label: offset === 0 ? `${year} (Current)` : `${year}`,
					data,
					borderColor: color.border,
					backgroundColor: color.bg,
					borderWidth: offset === 0 ? 3 : 2,
					pointRadius: offset === 0 ? 3 : 1,
					pointHoverRadius: offset === 0 ? 5 : 3,
					borderDash: offset === 0 ? [] : [5, 5],
					fill: false,
					tension: 0.1,
					_meta: { kind: "total", yearOffset: offset },
				})
			})

		// Optional overlay: Top 5 sites + Other (current vs last year)
		if (showSiteBreakdown && processedSiteData[0] && processedSiteData[1]) {
			const overlaySites = [...siteOverlay.topSites, ...(siteOverlay.hasOther ? ["Other"] : [])]

			overlaySites.forEach((siteKey, idx) => {
				;[0, 1].forEach((yearOffset) => {
					const isCurrent = yearOffset === 0
					const counts = siteKey === "Other" ? siteOverlay.otherByYear[yearOffset]?.cumulativeCounts || {} : processedSiteData[yearOffset]?.cumulativeCountsBySite?.[siteKey] || {}

					const data = allDates.map((date) => {
						// For current year viewing current month, don't show future dates
						if (isCurrent && isCurrentMonth && date > today) {
							return null
						}

						return counts[date] || 0
					})

					datasets.push({
						label: `${siteKey} (${isCurrent ? "Current" : "Last year"})`,
						data,
						borderColor: getColorForSite(idx, isCurrent ? 0.25 : 0.18),
						backgroundColor: getColorForSite(idx, isCurrent ? 0.25 : 0.18),
						borderWidth: 1,
						pointRadius: 0,
						pointHoverRadius: 3,
						borderDash: isCurrent ? [] : [4, 4],
						fill: false,
						tension: 0.1,
						_meta: { kind: "site", yearOffset, siteKey },
					})
				})
			})
		}

		return { labels: allDates, datasets }
	}, [enquiriesByYear, processedData, processedSiteData, siteOverlay, allDates, today, isCurrentMonth, showSiteBreakdown])

	// Chart options with memoization
	const chartOptions = useMemo(
		() => ({
			responsive: true,
			maintainAspectRatio: false,
			animations: {
				y: {
					easing: "easeOutQuart",
					duration: 1500,
				},
				x: {
					duration: 0,
				},
			},
			plugins: {
				legend: {
					position: "top",
					labels: {
						usePointStyle: true,
						padding: 15,
						font: {
							size: 12,
							weight: "500",
						},
					},
				},
				tooltip: {
					enabled: true,
					mode: "index",
					intersect: false,
					backgroundColor: "rgba(255, 255, 255, 0.95)",
					titleColor: "#111827",
					bodyColor: "#374151",
					borderColor: "#e5e7eb",
					borderWidth: 1,
					padding: 12,
					boxPadding: 6,
					usePointStyle: true,
					callbacks: {
						label: function (context) {
							const value = context.raw
							const date = context.label
							const meta = context.dataset?._meta || {}

							// Skip if value is null
							if (value === null || value === undefined) {
								return null
							}

							// Get the daily total for this date and dataset
							let dailyTotal = 0
							if (meta.kind === "site") {
								if (meta.siteKey === "Other") {
									dailyTotal = siteOverlay.otherByYear?.[meta.yearOffset]?.dailyCounts?.[date] || 0
								} else {
									dailyTotal = processedSiteData?.[meta.yearOffset]?.dailyCountsBySite?.[meta.siteKey]?.[date] || 0
								}
							} else {
								dailyTotal = processedData?.[meta.yearOffset]?.dailyCounts?.[date] || 0
							}

							// Compare totals vs current year total
							if (meta.kind === "total" && meta.yearOffset !== 0 && chartData.datasets[0]) {
								const currentYearValue = chartData.datasets[0].data[context.dataIndex]

								// Skip comparison if current year value is null or undefined
								if (currentYearValue === null || currentYearValue === undefined) {
									return [`${context.dataset.label}: ${value}`, `Daily Total: ${dailyTotal}`]
								}

								const diff = currentYearValue - value
								const percentageDiff = value ? ((diff / value) * 100).toFixed(1) : "N/A"
								return [`${context.dataset.label}: ${value} (${diff >= 0 ? "+" : ""}${percentageDiff}% vs current)`, `Daily Total: ${dailyTotal}`]
							}

							// Compare site last year vs current for same site
							if (meta.kind === "site" && meta.yearOffset === 1) {
								const currentSiteDataset = chartData.datasets.find((ds) => {
									return ds?._meta?.kind === "site" && ds?._meta?.yearOffset === 0 && ds?._meta?.siteKey === meta.siteKey
								})

								if (currentSiteDataset) {
									const currentValue = currentSiteDataset.data[context.dataIndex]
									if (currentValue === null || currentValue === undefined) {
										return [`${context.dataset.label}: ${value}`, `Daily Total: ${dailyTotal}`]
									}

									const diff = currentValue - value
									const percentageDiff = value ? ((diff / value) * 100).toFixed(1) : "N/A"
									return [`${context.dataset.label}: ${value} (${diff >= 0 ? "+" : ""}${percentageDiff}% vs current)`, `Daily Total: ${dailyTotal}`]
								}
							}

							return [`${context.dataset.label}: ${value}`, `Daily Total: ${dailyTotal}`]
						},
					},
				},
			},
			interaction: {
				mode: "index",
				intersect: false,
			},
			scales: {
				x: {
					title: {
						display: true,
						text: "Date (DD/MM)",
						font: { size: 12, weight: "600" },
						color: "#374151",
					},
					grid: {
						color: "rgba(229, 231, 235, 0.5)",
						drawBorder: false,
					},
					ticks: {
						maxRotation: 45,
						minRotation: 45,
						font: { size: 10 },
						color: "#6b7280",
					},
				},
				y: {
					title: {
						display: true,
						text: "Cumulative Enquiries",
						font: { size: 12, weight: "600" },
						color: "#374151",
					},
					grid: {
						color: "rgba(229, 231, 235, 0.5)",
						drawBorder: false,
					},
					ticks: {
						font: { size: 10 },
						color: "#6b7280",
					},
				},
			},
		}),
		[chartData, processedData, processedSiteData, siteOverlay]
	)

	if (loading) {
		return (
			<div>
				<h2>Enquiry Trends</h2>
				<p>Loading data...</p>
			</div>
		)
	}

	return (
		<div>
			<h2>Enquiry Trends</h2>
			<label style={{ display: "flex", alignItems: "center", gap: "8px", margin: "8px 0 12px" }}>
				<input
					type="checkbox"
					checked={showSiteBreakdown}
					onChange={(e) => setShowSiteBreakdown(e.target.checked)}
				/>
				Show site breakdown (Top 5 + Other)
			</label>
			{Object.keys(enquiriesByYear)
				.sort((a, b) => parseInt(a) - parseInt(b))
				.map((yearOffset) => {
					const offset = parseInt(yearOffset)
					const data = enquiriesByYear[offset]
					return (
						<p key={offset}>
							{offset === 0 ? "Current" : `${data.year}`}: {data.count}
						</p>
					)
				})}
			<br />
			<div style={{ height: "600px", width: "100%" }}>
				<Line
					data={chartData}
					options={chartOptions}
				/>
			</div>
		</div>
	)
}

export default memo(EnquiriesLineChart)
